import { useEffect, useMemo } from 'react'

import { UseSelectOptions } from '../types'
import { useCounter } from './useCounter'
import { useFetch } from './useFetch'
import { useInteractions } from './useInteractions'
import { useKeyboardNav } from './useKeyboardNav'
import { useManageState } from './useManageState'
import { useRequired } from './useRequired'
import { useTyping } from './useTyping'

export const useSelect = (options: UseSelectOptions) => {
  const {
    selectedValues,
    data: _data,
    listRef,
    inputRef,
    serverOptions,
    required,
    error,
    disabled,
    mode,
    searchable,
    selectionCallback
  } = options

  const {
    initialData,
    setSearchedData,
    searchText,
    setSearchText,
    isSearching,
    setIsSearching,
    activeIndex,
    setActiveIndex,
    selectValue,
    unselectValue,
    modelData,
    open,
    handleOpen,
    handleClose,
    inputValue,
    selectAllValues,
    unselectAllValues
  } = useManageState(_data ?? [], selectedValues, inputRef, selectionCallback)

  useEffect(() => {
    listRef.current = listRef.current.slice(0, initialData.length)
    // run effect on data change only
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialData])

  /* for internal use */
  const context = useMemo(
    () => ({
      open,
      initialData,
      setSearchedData,
      modelData,
      selectValue,
      unselectValue,
      selectedValues,
      activeIndex,
      setActiveIndex,
      searchText,
      setSearchText,
      setIsSearching,
      required,
      error,
      disabled,
      searchable,
      selectAllValues,
      unselectAllValues
    }),
    [
      open,
      initialData,
      setSearchedData,
      JSON.stringify(modelData),
      selectValue,
      unselectValue,
      selectedValues,
      activeIndex,
      setActiveIndex,
      searchText,
      setSearchText,
      setIsSearching,
      required,
      error,
      disabled,
      searchable,
      selectAllValues,
      unselectAllValues
    ]
  )

  const { count } = useCounter(context)
  const { isShowRequired, isEmpty } = useRequired(context)

  useFetch(context, { serverOptions })

  const { getReferenceProps, getOptionProps, getInputProps } = useInteractions([
    useTyping(context, { isPreferServerData: serverOptions !== undefined }),
    useKeyboardNav(context, { mode, inputRef, listRef })
  ])

  return {
    open,
    initialData,
    modelData,
    count,
    selectedValues,
    activeIndex,
    searchText,
    isSearching,
    isShowRequired,
    isEmpty,
    getReferenceProps,
    getOptionProps,
    getInputProps,
    selectValue,
    unselectValue,
    handleOpen,
    handleClose,
    inputValue,
    selectAllValues,
    unselectAllValues
  }
}
